'use client';

import { useEffect } from 'react';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { DataLayerKey, useBasketProductsList, useDataLayer } from '@headless-workspace/domain/common/client';
import { ProductDetailValue } from '@headless-workspace/domain/common/server';
import { Component } from '@headless-workspace/typings';
import { ProductDetailDataLayerUtils } from './ProductDetailDataLayerUtils';

type ProductDetailTemplateDidMountProps = {
    product: ProductDetailValue;
};

export const ProductDetailTemplateDidMount: Component<ProductDetailTemplateDidMountProps> = ({ product }) => {
    const { addVariables } = useDataLayer();

    // Retrieve customer's basket
    const { result: customerBasketResult } = useBasketProductsList();
    const customerBasket = unwrapResultSuccess(customerBasketResult);

    const formattedVariables = ProductDetailDataLayerUtils.formattedVariables(product, customerBasket);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        addVariables({
            [DataLayerKey.EnvTemplate]: formattedVariables.envTemplate,
            [DataLayerKey.PageName]: formattedVariables.pageName,
            [DataLayerKey.PageTopCat]: formattedVariables.pageCategory.topCat,
            [DataLayerKey.PageCat]: formattedVariables.pageCategory.cat,
            [DataLayerKey.PageSubCat]: formattedVariables.pageCategory.subCat,
            [DataLayerKey.ProductBreadcrumbLabel]: formattedVariables.breadcrumbJoined,
            [DataLayerKey.PageTrademark]: formattedVariables.brandName,
            [DataLayerKey.ProductAxis]: formattedVariables.productAxis,
            [DataLayerKey.ProductBrand]: formattedVariables.brandName,
            [DataLayerKey.ProductBreadcrumbId]: formattedVariables.breadcrumbIds,
            [DataLayerKey.ProductCop]: formattedVariables.hasDiscount,
            [DataLayerKey.ProductCurrency]: formattedVariables.currency,
            [DataLayerKey.ProductCustomizable]: formattedVariables.isCustomizable,
            [DataLayerKey.ProductCustomized]: formattedVariables.isCustomized,
            [DataLayerKey.ProductFidelityPoints]: product.loyalty?.pointsToEarn,
            [DataLayerKey.ProductInstock]: formattedVariables.inStock,
            [DataLayerKey.ProductNature]: formattedVariables.productNature,
            [DataLayerKey.ProductNumRating]: formattedVariables.reviewCountRange,
            [DataLayerKey.ProductPid]: formattedVariables.productPid,
            [DataLayerKey.ProductPidName]: product.name?.full,
            [DataLayerKey.ProductPriceAti]: product.price,
            [DataLayerKey.ProductPromotion]: formattedVariables.productPromotion,
            [DataLayerKey.ProductRange]: formattedVariables.productRange,
            [DataLayerKey.ProductRating]: product.rating,
            [DataLayerKey.ProductSku]: formattedVariables.productSku,
            [DataLayerKey.ProductSkuName]: formattedVariables.skuName,
            [DataLayerKey.ProductVideo]: formattedVariables.hasVideo,
        });
    }, [
        product,
        addVariables,
        formattedVariables.brandName,
        formattedVariables.hasDiscount,
        formattedVariables.isCustomizable,
        formattedVariables.isCustomized,
        formattedVariables.inStock,
        formattedVariables.productPromotion,
        formattedVariables.hasTravelSize,
        formattedVariables.hasVideo,
        formattedVariables.pageCategory.topCat,
        formattedVariables.pageCategory.cat,
        formattedVariables.pageCategory.subCat,
        formattedVariables.pageName,
        formattedVariables.breadcrumbJoined,
        formattedVariables.productAxis,
        formattedVariables.breadcrumbIds,
        formattedVariables.currency,
        formattedVariables.productNature,
        formattedVariables.reviewCountRange,
        formattedVariables.productRange,
        formattedVariables.skuName,
        formattedVariables.envTemplate,
        formattedVariables.productPid,
        formattedVariables.productSku,
    ]);

    return null;
};
